/**
 * Плагин трансформации данных для CSElement
 * Обеспечивает преобразование данных при операциях
 */

import { CSElementPlugin, LifecycleHooks, PluginOptions } from '../types/plugin-interfaces';
import { ICSElement } from '../types/interfaces';
import { CSElement } from '../core/CSElement';

/**
 * Типы трансформации
 */
export enum TransformType {
  INPUT = 'input',      // При входе данных
  OUTPUT = 'output',    // При выходе данных
  STORAGE = 'storage',  // При сохранении
  DISPLAY = 'display',   // При отображении
  JSON_STRINGIFY = 'json_stringify',
  JSON_PARSE = 'json_parse',
  BASE64_ENCODE = 'base64_encode',
  BASE64_DECODE = 'base64_decode',
  UPPERCASE = 'uppercase',
  LOWERCASE = 'lowercase',
  CUSTOM = 'custom'
}

/**
 * Направление трансформации
 */
export enum TransformDirection {
  ENCODE = 'encode',    // Кодирование
  DECODE = 'decode',    // Декодирование
  BOTH = 'both'
}

/**
 * Функция трансформации
 */
export type TransformFunction<T = any, R = any> = (
  value: T,
  context: TransformContext
) => R | Promise<R>;

/**
 * Контекст трансформации
 */
export interface TransformContext {
  element: ICSElement;
  key: string;
  type: TransformType;
  direction: TransformDirection;
  metadata: Record<string, any>;
  originalValue: any;
}

/**
 * Правило трансформации
 */
export interface TransformRule {
  name: string;
  type: TransformType;
  direction: TransformDirection;
  keyPattern?: string | RegExp;
  valueType?: string;
  condition?: (context: TransformContext) => boolean;
  transform: TransformFunction;
  priority?: number;
  enabled?: boolean;
  field?: string;
  transformer?: (value: any) => any;
}

/**
 * Результат трансформации
 */
export interface TransformResult<T = any> {
  success: boolean;
  value: T;
  error?: string;
  metadata?: Record<string, any>;
  appliedRules: string[];
}

/**
 * Конфигурация трансформации
 */
export interface TransformConfig {
  enabled: boolean;
  enabledTypes: TransformType[];
  rules: TransformRule[];
  maxTransformTime: number;  // Максимальное время трансформации в мс
  enableCache: boolean;      // Кэширование результатов
  enableStats: boolean;      // Статистика трансформаций
}

/**
 * Статистика трансформаций
 */
export interface TransformStats {
  totalTransforms: number;
  successfulTransforms: number;
  failedTransforms: number;
  averageTime: number;
  ruleStats: Map<string, {
    executions: number;
    successes: number;
    failures: number;
    totalTime: number;
    averageTime: number;
  }>;
}

/**
 * Плагин трансформации
 */
export class TransformPlugin implements CSElementPlugin {
  public readonly name = 'TransformPlugin';
  public readonly version = '1.0.0';
  public readonly description = 'Плагин для трансформации данных';

  private config: TransformConfig = {
    enabled: true,
    enabledTypes: [TransformType.INPUT, TransformType.OUTPUT, TransformType.STORAGE],
    rules: [],
    maxTransformTime: 5000, // 5 секунд
    enableCache: true,
    enableStats: true
  };

  private transformCache = new Map<string, any>();
  private stats: TransformStats = {
    totalTransforms: 0,
    successfulTransforms: 0,
    failedTransforms: 0,
    averageTime: 0,
    ruleStats: new Map()
  };

  constructor(options?: PluginOptions) {
    if (options?.config) {
      this.config = { ...this.config, ...options.config };
    }

    // Добавляем встроенные трансформации
    this.addBuiltInTransforms();
  }

  install(CSElementClass: typeof CSElement): void {
    console.log(`TransformPlugin установлен для ${CSElementClass.name}`);
  }

  getLifecycleHooks(): LifecycleHooks {
    return {
      beforeSetData: async (element: ICSElement, key: string, value: any) => {
        if (this.config.enabled && this.config.enabledTypes.includes(TransformType.INPUT)) {
          const context: TransformContext = {
            element,
            key,
            type: TransformType.INPUT,
            direction: TransformDirection.ENCODE,
            metadata: {},
            originalValue: value
          };

          const result = await this.applyTransforms(value, context);
          if (result.success && result.value !== value) {
            // Заменяем значение на трансформированное
            // Это требует модификации оригинального значения
            Object.assign(arguments[2], result.value);
          }
        }
      }
    };
  }

  /**
   * Применение трансформаций
   */
  async applyTransforms(value: any, context: TransformContext): Promise<TransformResult> {
    if (!this.config.enabled || !this.config.enabledTypes.includes(context.type)) {
      return {
        success: true,
        value,
        appliedRules: []
      };
    }

    const startTime = Date.now();
    let currentValue = value;
    const appliedRules: string[] = [];
    let error: string | undefined;

    try {
      // Получаем применимые правила
      const applicableRules = this.getApplicableRules(context);

      // Применяем правила по приоритету
      for (const rule of applicableRules) {
        if (!rule.enabled) {
          continue;
        }

        try {
          const ruleStartTime = Date.now();
          
          // Проверяем условие
          if (rule.condition && !rule.condition(context)) {
            continue;
          }

          // Применяем трансформацию с таймаутом
          const transformedValue = await this.applyTransformWithTimeout(
            rule.transform,
            currentValue,
            context
          );

          currentValue = transformedValue;
          appliedRules.push(rule.name);

          // Обновляем статистику правила
          if (this.config.enableStats) {
            this.updateRuleStats(rule.name, Date.now() - ruleStartTime, true);
          }

        } catch (ruleError) {
          if (this.config.enableStats) {
            this.updateRuleStats(rule.name, Date.now() - startTime, false);
          }
          
          error = `Ошибка в правиле ${rule.name}: ${ruleError instanceof Error ? ruleError.message : String(ruleError)}`;
          break;
        }
      }

      // Обновляем общую статистику
      if (this.config.enableStats) {
        this.updateStats(Date.now() - startTime, !error);
      }

      return {
        success: !error,
        value: currentValue,
        error,
        appliedRules
      };

    } catch (transformError) {
      if (this.config.enableStats) {
        this.updateStats(Date.now() - startTime, false);
      }

      return {
        success: false,
        value,
        error: transformError instanceof Error ? transformError.message : String(transformError),
        appliedRules
      };
    }
  }

  /**
   * Получение применимых правил
   */
  private getApplicableRules(context: TransformContext): TransformRule[] {
    return this.config.rules
      .filter(rule => 
        rule.type === context.type &&
        rule.direction === context.direction &&
        this.matchesKeyPattern(context.key, rule.keyPattern) &&
        this.matchesValueType(context.originalValue, rule.valueType)
      )
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }

  /**
   * Проверка соответствия паттерну ключа
   */
  private matchesKeyPattern(key: string, pattern?: string | RegExp): boolean {
    if (!pattern) {
      return true;
    }

    if (typeof pattern === 'string') {
      return pattern === '*' || key === pattern || key.includes(pattern);
    }

    if (pattern instanceof RegExp) {
      return pattern.test(key);
    }

    return false;
  }

  /**
   * Проверка соответствия типу значения
   */
  private matchesValueType(value: any, type?: string): boolean {
    if (!type) {
      return true;
    }

    const actualType = typeof value;
    return actualType === type || type === '*';
  }

  /**
   * Применение трансформации с таймаутом
   */
  private async applyTransformWithTimeout(
    transform: TransformFunction,
    value: any,
    context: TransformContext
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Трансформация превысила лимит времени ${this.config.maxTransformTime}мс`));
      }, this.config.maxTransformTime);

      Promise.resolve(transform(value, context))
        .then(result => {
          clearTimeout(timeout);
          resolve(result);
        })
        .catch(error => {
          clearTimeout(timeout);
          reject(error);
        });
    });
  }

  /**
   * Добавление правила трансформации
   */
  addRule(rule: TransformRule): void {
    // Проверяем уникальность имени
    const existingIndex = this.config.rules.findIndex(r => r.name === rule.name);
    if (existingIndex >= 0) {
      this.config.rules[existingIndex] = rule;
    } else {
      this.config.rules.push(rule);
    }
  }

  /**
   * Удаление правила трансформации
   */
  removeRule(name: string): boolean {
    const index = this.config.rules.findIndex(r => r.name === name);
    if (index >= 0) {
      this.config.rules.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Получение правила по имени
   */
  getRule(name: string): TransformRule | undefined {
    return this.config.rules.find(r => r.name === name);
  }

  /**
   * Получение всех правил
   */
  getRules(): TransformRule[] {
    return [...this.config.rules];
  }

  /**
   * Включение/отключение правила
   */
  toggleRule(name: string, enabled: boolean): boolean {
    const rule = this.config.rules.find(r => r.name === name);
    if (rule) {
      rule.enabled = enabled;
      return true;
    }
    return false;
  }

  /**
   * Трансформация значения напрямую
   */
  async transform(
    value: any,
    type: TransformType,
    direction: TransformDirection,
    element: ICSElement,
    key: string = 'direct'
  ): Promise<TransformResult> {
    const context: TransformContext = {
      element,
      key,
      type,
      direction,
      metadata: {},
      originalValue: value
    };

    return this.applyTransforms(value, context);
  }

  /**
   * Добавление встроенных трансформаций
   */
  private addBuiltInTransforms(): void {
    // Трансформация строк
    this.addRule({
      name: 'string_trim',
      type: TransformType.INPUT,
      direction: TransformDirection.ENCODE,
      valueType: 'string',
      transform: (value: string) => value.trim(),
      priority: 100,
      enabled: true
    });

    this.addRule({
      name: 'string_lowercase',
      type: TransformType.INPUT,
      direction: TransformDirection.ENCODE,
      keyPattern: /.*_lower$/,
      valueType: 'string',
      transform: (value: string) => value.toLowerCase(),
      priority: 90,
      enabled: true
    });

    this.addRule({
      name: 'string_uppercase',
      type: TransformType.INPUT,
      direction: TransformDirection.ENCODE,
      keyPattern: /.*_upper$/,
      valueType: 'string',
      transform: (value: string) => value.toUpperCase(),
      priority: 90,
      enabled: true
    });

    // Трансформация чисел
    this.addRule({
      name: 'number_round',
      type: TransformType.INPUT,
      direction: TransformDirection.ENCODE,
      keyPattern: /.*_round$/,
      valueType: 'number',
      transform: (value: number) => Math.round(value),
      priority: 80,
      enabled: true
    });

    this.addRule({
      name: 'number_abs',
      type: TransformType.INPUT,
      direction: TransformDirection.ENCODE,
      keyPattern: /.*_abs$/,
      valueType: 'number',
      transform: (value: number) => Math.abs(value),
      priority: 80,
      enabled: true
    });

    // Трансформация дат
    this.addRule({
      name: 'date_iso_string',
      type: TransformType.OUTPUT,
      direction: TransformDirection.ENCODE,
      keyPattern: /.*_date$/,
      condition: (context) => context.originalValue instanceof Date,
      transform: (value: Date) => value.toISOString(),
      priority: 70,
      enabled: true
    });

    this.addRule({
      name: 'date_parse',
      type: TransformType.INPUT,
      direction: TransformDirection.DECODE,
      keyPattern: /.*_date$/,
      valueType: 'string',
      transform: (value: string) => new Date(value),
      priority: 70,
      enabled: true
    });

    // JSON трансформации
    this.addRule({
      name: 'json_stringify',
      type: TransformType.STORAGE,
      direction: TransformDirection.ENCODE,
      keyPattern: /.*_json$/,
      condition: (context) => typeof context.originalValue === 'object',
      transform: (value: any) => JSON.stringify(value),
      priority: 60,
      enabled: true
    });

    this.addRule({
      name: 'json_parse',
      type: TransformType.STORAGE,
      direction: TransformDirection.DECODE,
      keyPattern: /.*_json$/,
      valueType: 'string',
      transform: (value: string) => {
        try {
          return JSON.parse(value);
        } catch {
          return value; // Возвращаем исходное значение если не удалось распарсить
        }
      },
      priority: 60,
      enabled: true
    });

    // Sanitization
    this.addRule({
      name: 'html_escape',
      type: TransformType.INPUT,
      direction: TransformDirection.ENCODE,
      keyPattern: /.*_html$/,
      valueType: 'string',
      transform: (value: string) => {
        return value
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      },
      priority: 50,
      enabled: true
    });

    // URL encoding
    this.addRule({
      name: 'url_encode',
      type: TransformType.OUTPUT,
      direction: TransformDirection.ENCODE,
      keyPattern: /.*_url$/,
      valueType: 'string',
      transform: (value: string) => encodeURIComponent(value),
      priority: 40,
      enabled: true
    });

    this.addRule({
      name: 'url_decode',
      type: TransformType.INPUT,
      direction: TransformDirection.DECODE,
      keyPattern: /.*_url$/,
      valueType: 'string',
      transform: (value: string) => decodeURIComponent(value),
      priority: 40,
      enabled: true
    });
  }

  /**
   * Обновление статистики
   */
  private updateStats(duration: number, success: boolean): void {
    this.stats.totalTransforms++;
    
    if (success) {
      this.stats.successfulTransforms++;
    } else {
      this.stats.failedTransforms++;
    }

    // Обновляем среднее время
    const totalTime = this.stats.averageTime * (this.stats.totalTransforms - 1) + duration;
    this.stats.averageTime = totalTime / this.stats.totalTransforms;
  }

  /**
   * Обновление статистики правила
   */
  private updateRuleStats(ruleName: string, duration: number, success: boolean): void {
    let ruleStats = this.stats.ruleStats.get(ruleName);
    if (!ruleStats) {
      ruleStats = {
        executions: 0,
        successes: 0,
        failures: 0,
        totalTime: 0,
        averageTime: 0
      };
      this.stats.ruleStats.set(ruleName, ruleStats);
    }

    ruleStats.executions++;
    ruleStats.totalTime += duration;
    ruleStats.averageTime = ruleStats.totalTime / ruleStats.executions;

    if (success) {
      ruleStats.successes++;
    } else {
      ruleStats.failures++;
    }
  }

  /**
   * Получение статистики
   */
  getStats(): TransformStats {
    return {
      ...this.stats,
      ruleStats: new Map(this.stats.ruleStats)
    };
  }

  /**
   * Сброс статистики
   */
  resetStats(): void {
    this.stats = {
      totalTransforms: 0,
      successfulTransforms: 0,
      failedTransforms: 0,
      averageTime: 0,
      ruleStats: new Map()
    };
  }

  /**
   * Очистка кэша
   */
  clearCache(): void {
    this.transformCache.clear();
  }

  /**
   * Обновление конфигурации
   */
  updateConfig(config: Partial<TransformConfig>): void {
    this.config = { ...this.config, ...config };
    
    if (!this.config.enableCache) {
      this.clearCache();
    }
  }

  /**
   * Получение конфигурации
   */
  getConfig(): TransformConfig {
    return { ...this.config };
  }
}

/**
 * Фабричная функция для создания плагина трансформации
 */
export function transformPlugin(config?: Partial<TransformConfig>): TransformPlugin {
  return new TransformPlugin({ config });
} 